import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { BellOff, CheckCircle2, XCircle } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { unsubscribe, unsubscribeInfo } from "@/lib/api";

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const id = params.get("id") || "";
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    document.title = "Uitschrijven | WanneerKaartverkoop.nl";
    window.scrollTo(0, 0);
    if (!id) {
      setState("error");
      return;
    }
    unsubscribeInfo(id)
      .then((i) => {
        setInfo(i);
        setState(i.unsubscribed ? "done" : "ready");
      })
      .catch(() => setState("error"));
  }, [id]);

  const confirm = async () => {
    try {
      await unsubscribe(id);
      setState("done");
    } catch {
      setState("error");
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-obsidian text-white" data-testid="unsubscribe-page">
      <Header onOrganizer={() => nav("/contact")} />
      <main className="flex flex-1 items-center justify-center px-4 pt-16 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md rounded-3xl border border-white/10 bg-panel p-8 text-center"
        >
          {state === "loading" && (
            <div className="mx-auto h-8 w-8 animate-pulse rounded-full bg-white/10" />
          )}
          {state === "ready" && (
            <>
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blaze/15">
                <BellOff className="h-6 w-6 text-blaze" />
              </span>
              <h1 className="mt-5 font-display text-2xl font-bold tracking-tight">Uitschrijven?</h1>
              <p className="mt-3 text-sm leading-relaxed text-slate-400" data-testid="unsubscribe-question">
                Je schrijft je uit voor{" "}
                <span className="font-semibold text-white">
                  {info?.event_name ? `herinneringen voor ${info.event_name}` : "alle kaartverkoop-alerts"}
                </span>
                {info?.channel === "whatsapp" ? " via WhatsApp" : " via e-mail"}. Je ontvangt daarna geen
                seintjes meer.
              </p>
              <button
                onClick={confirm}
                data-testid="unsubscribe-confirm-button"
                className="mt-6 w-full rounded-full bg-blaze py-3 text-sm font-bold text-black transition-transform hover:scale-[1.02] hover:bg-blaze-hover"
              >
                Ja, schrijf mij uit
              </button>
              <Link to="/" className="mt-3 inline-block text-xs text-slate-500 hover:text-slate-300">
                Toch houden — terug naar de site
              </Link>
            </>
          )}
          {state === "done" && (
            <>
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-volt/10">
                <CheckCircle2 className="h-6 w-6 text-volt" />
              </span>
              <h1 className="mt-5 font-display text-2xl font-bold tracking-tight" data-testid="unsubscribe-success">
                Je bent uitgeschreven
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                Je ontvangt geen herinneringen meer van ons. Verander je van gedachten? Meld je gewoon
                opnieuw aan bij een event.
              </p>
              <Link
                to="/"
                className="mt-6 inline-block rounded-full bg-volt px-6 py-2.5 text-sm font-bold text-black transition-transform hover:scale-[1.02]"
              >
                Terug naar de site
              </Link>
            </>
          )}
          {state === "error" && (
            <>
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#FF3B30]/15">
                <XCircle className="h-6 w-6 text-[#FF6B60]" />
              </span>
              <h1 className="mt-5 font-display text-2xl font-bold tracking-tight" data-testid="unsubscribe-error">
                Ongeldige link
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                Deze uitschrijflink is ongeldig of al gebruikt. Lukt het niet? Mail ons via{" "}
                <a href="mailto:info@wanneerkaartverkoop.nl" className="text-blaze hover:underline">
                  info@wanneerkaartverkoop.nl
                </a>
                , dan halen we je er handmatig af.
              </p>
            </>
          )}
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
